import {
  GcpBox,
  GcpInvitationTable,
  GcpTypography,
  PH_ISD_CODE,
} from '@back-office/ui';
import { FormLabel } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import TextFields from '@material-ui/core/TextField';
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded';
import InfoSharpIcon from '@material-ui/icons/InfoSharp';
import SearchIcon from '@material-ui/icons/Search';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InviteNewUserFieldNames } from '../../forms';
import useStyles from './styles';
import useViewModel from './viewmodel';

const InviteNewUser: FC = observer(() => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    control,
    handleSubmit,
    errors,
    clearErrors,
    getValues,
    invitations,
    isPhoneNumberExist,
    showSuccessMessage,
    emailToTimerMap,
    handleOnSubmit,
    validateForm,
    handleSearchPhoneNumber,
    openResendAlert,
    openRemoveAlert,
    enableSubmitButton,
  } = useViewModel();

  // isPhoneNumberExist can be true, false and undefined
  const renderResponseMessage = (): React.ReactNode => (
    <GcpBox>
      {isPhoneNumberExist === true && (
        <GcpBox style={{ color: 'green' }}>
          <CheckCircleOutlineRoundedIcon fontSize="large" />
          &nbsp;
          {t(`invite_new_user.mobile_number_exist`)}
        </GcpBox>
      )}
      {isPhoneNumberExist === false && (
        <GcpBox style={{ color: 'red' }}>
          <InfoSharpIcon fontSize="large" />
          &nbsp;
          {t(`invite_new_user.mobile_number_not_exist`)}
        </GcpBox>
      )}
      <GcpBox>{t('invite_new_user.expire_mobile')}</GcpBox>
    </GcpBox>
  );

  return (
    <GcpBox height="100%">
      {showSuccessMessage === true && (
        <GcpBox
          className={classes.success_popup}
          style={{ backgroundColor: '#e8f5ee' }}
        >
          {t(`invite_new_user.invite_success_message`, {
            email: getValues().email,
          })}
        </GcpBox>
      )}
      {showSuccessMessage === false && (
        <GcpBox
          className={classes.success_popup}
          style={{ backgroundColor: '#fdeeee' }}
        >
          {t(`invite_new_user.invite_failed_message`, {
            email: getValues().email,
          })}
        </GcpBox>
      )}
      <GcpTypography variant="h3">{t('invite_new_user.header')}</GcpTypography>
      <GcpBox mt={3}>
        <Divider />
      </GcpBox>
      <form
        autoComplete="off"
        onChange={validateForm}
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <GcpBox mt={3}>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <GcpBox display="flex" justifyContent="space-between">
                <GcpBox className={classes.input_box} mb={2.5}>
                  <FormLabel
                    className={classes.label}
                    htmlFor={InviteNewUserFieldNames.Email}
                  >
                    {t('invite_new_user.add_email')}
                  </FormLabel>
                  <Controller
                    control={control}
                    defaultValue=""
                    name={InviteNewUserFieldNames.Email}
                    render={({ onChange, name, value, onBlur }) => (
                      <GcpBox display="flex">
                        <TextFields
                          className={classes.email}
                          error={!!errors.email?.type}
                          helperText={
                            errors.email?.message || t('invite_new_user.expire')
                          }
                          label={t('invite_new_user.email_field')}
                          name={name}
                          type="text"
                          value={value}
                          variant="outlined"
                          onBlur={onBlur}
                          onChange={onChange}
                          onClick={() =>
                            clearErrors(InviteNewUserFieldNames.Email)
                          }
                          onKeyPress={(event) => {
                            event.key === 'Enter' && handleSearchPhoneNumber();
                          }}
                        />
                        <Button
                          className={classes.search_icon_box}
                          onClick={handleSearchPhoneNumber}
                        >
                          <SearchIcon className={classes.search_icon} />
                        </Button>
                      </GcpBox>
                    )}
                  />
                </GcpBox>
                <GcpBox className={classes.input_box} mb={2.5}>
                  <FormLabel
                    className={classes.label}
                    htmlFor={InviteNewUserFieldNames.Email}
                  >
                    {t('invite_new_user.add_mobile')}
                  </FormLabel>
                  <Controller
                    control={control}
                    defaultValue=""
                    name={InviteNewUserFieldNames.Phone}
                    render={({ onChange, name, value, onBlur }) => (
                      <GcpBox display="flex">
                        <GcpBox className={classes.isd_code}>
                          {PH_ISD_CODE}
                        </GcpBox>
                        <TextFields
                          className={classes.phone}
                          disabled={isPhoneNumberExist}
                          error={!!errors.email?.type}
                          helperText={renderResponseMessage()}
                          label={t('invite_new_user.mobile_number_lower')}
                          name={name}
                          style={
                            isPhoneNumberExist ? { background: '#e6e1e1' } : {}
                          }
                          type="text"
                          value={value}
                          variant="outlined"
                          onBlur={onBlur}
                          onChange={onChange}
                          onClick={() =>
                            clearErrors(InviteNewUserFieldNames.Email)
                          }
                        />
                      </GcpBox>
                    )}
                  />
                </GcpBox>
              </GcpBox>
            </Grid>
            <Grid item className={classes.top} xs={2}>
              <Button
                classes={{ root: classes.button }}
                color="primary"
                disabled={!enableSubmitButton}
                type="submit"
                variant="contained"
              >
                {t('invite_new_user.send')}
              </Button>
            </Grid>
          </Grid>
        </GcpBox>
      </form>
      <GcpBox mt={6}>
        <GcpTypography variant="h3">
          {t('invite_new_user.invited_users')}
        </GcpTypography>
      </GcpBox>
      <GcpBox mt={2}>
        <GcpInvitationTable
          data={invitations.list}
          emailToTimerMap={emailToTimerMap}
          handleOnClickRemove={openRemoveAlert}
          handleOnClickResend={openResendAlert}
        />
      </GcpBox>
    </GcpBox>
  );
});

export default InviteNewUser;
